/**
 * A custom HTML element that creates an expandable variant shelf with horizontal sliding functionality.
 *
 * This component displays product variants in a collapsible container with navigation buttons
 * for horizontal scrolling.
 *
 * @example
 * ```html
 * <awe-variant-shelf open="true">
 *   <div class="shelf-pdt">Product 1</div>
 *   <div class="shelf-pdt">Product 2</div>
 *   <div class="shelf-pdt">Product 3</div>
 * </awe-variant-shelf>
 * ```
 */
class AweVariantShelf extends HTMLElement {
    constructor() {
        super();
        /** ResizeObserver instance for responsive behavior, null if not supported */
        this.resizeObs = null;
        this.open = false;
        this.transitionDuration = 400;
        this.productCount = 0;
        this.maxOffset = 0;
        this._offset = 0;
        const style = document.createElement('style');
        const btnNd = document.createElement('button');
        btnNd.classList.add('cta');
        btnNd.innerHTML = window.i18n.get('AWE-VARIANT-SHELF.CTA_LBL')
            + `<svg id="btn-picto" viewBox="-5,0 10,6">
                        <path d="M0,0 L5,6 L-5, 6Z"/>
                      </svg>`;
        btnNd.setAttribute('aria-label', window.i18n.get('AWE-VARIANT-SHELF.CTA_ARIA'));
        btnNd.addEventListener('click', this.toggle.bind(this));
        const ctnr = document.createElement('div');
        ctnr.classList.add('ctnr');
        ctnr.setAttribute('role', 'presentation');
        const content = document.createElement('div');
        content.classList.add('content');
        content.id = 'content';
        content.append(document.createElement('slot'));
        const leftBtn = document.createElement('button');
        leftBtn.classList.add('slide-btn', 'left');
        leftBtn.innerHTML = '<div class="picto" role="presentation"></div>';
        const rightBtn = leftBtn.cloneNode(true);
        rightBtn.classList.remove('left');
        rightBtn.classList.add('right');
        leftBtn.setAttribute('disabled', '');
        leftBtn.setAttribute('aria-label', window.i18n.get('AWE-VARIANT-SHELF.LEFT_ARIA'));
        rightBtn.setAttribute('aria-label', window.i18n.get('AWE-VARIANT-SHELF.RIGHT_ARIA'));
        leftBtn.addEventListener('click', () => { this.slide(-1); });
        rightBtn.addEventListener('click', () => { this.slide(1); });
        ctnr.append(leftBtn, content, rightBtn);
        style.textContent = `
    :host{
      position: relative;
      display: block;
      
      width: 100%;
      overflow: hidden;
      height: auto;
      --content-translate: 0%;
    }

    .cta{
      border:none;
      background: transparent;
      padding: 3px 5px;
      color: var(--main-color);
      font-family: var(--font-default);
      font-size: 1em;
      pointer: cursor;
      display: flex;
      align-items: center;
      margin: 1px;
    }
    .cta:focus{
      outline: 1px dotted black;
    }

    #btn-picto{
      width: 10px;
      margin-left: 5px;
      fill: var(--main-color);
    }
    #btn-picto.reversed{
      transform: scaleY(-1);
    }
    
    .ctnr{
      display: none;
      position: relative;
      justify-content: stretch;
      align-items: stretch;
      transform-origin: top center;
      transform: scaleY(0);
      transition: transform ${this.transitionDuration}ms ease-in-out;
      overflow: visible;
      padding-bottom: 10px;
      margin-top: 10px;
    }

    .ctnr.visible{
      display: block;
    }

    .ctnr.expanded{
      transform: scaleY(1);
    }

    .content{
      display: inline-flex;
      flex-flow: row nowrap;
      align-items: stretch;
      width: var(--content-width, auto);
      min-width: 100%;
      height: 200px;
      transform: translateX(var(--content-translate));
      transition: transform 400ms ease-in-out;

      --items-show: 2;
      --item-width: calc(100% / var(--items-show));
    }

    /* define some basic style for slotted nodes*/
    ::slotted(.shelf-pdt){
      width: var(--item-width);
    }

    .slide-btn{
      position: absolute;
      height: 40px;
      width: 40px;
      top: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      transform: translateY(-50%);
      cursor: pointer;
      background: none;
      border: none;
      padding: 0;
      z-index:10;
    }
    .slide-btn.left{
      left: 0;
    }
    .slide-btn.right{
      right: 0;
    }
    .slide-btn[disabled]{
      display: none;
    }

    .picto{
      background: url('../assets/8d1c299/img/2.0-arrow.svg') center no-repeat;
      height:100%;
      width:100%;
      display:block;
      
    }
    .left .picto{
      transform: scaleX(-1);
    }

    @media (min-width: 460px){
      .content{
        --items-show: 3; 
      }
    }

    @media (min-width: 620px){
      .content{
        --items-show: 4;
      }
    }

    @media (min-width: 700px){
      ::slotted(.shelf-pdt){
        width:140px;
      }
      .content{
        width: auto;
      }
    }
    `;
        this.attachShadow({ mode: 'open' });
        this.shadowRoot?.append(style, btnNd, ctnr);
        if (window.ResizeObserver) {
            this.resizeObs = new ResizeObserver(entries => {
                const ctnr = entries[0].target, content = ctnr.children[1];
                const maxOffset = Math.min(0, ctnr.offsetWidth - content.scrollWidth) / content.scrollWidth * 100;
                // disable right button if there is no need to scroll or scrolled to the max.
                const force = ctnr.offsetWidth >= ctnr.children[1].scrollWidth || maxOffset >= this._offset;
                this.shadowRoot?.querySelector('.right')?.toggleAttribute('disabled', force);
            });
            this.resizeObs.observe(ctnr);
        }
    }
    /**
     * Lifecycle callback invoked when the custom element is connected to the DOM.
     *
     * Initializes the component by:
     * - Reading the 'open' attribute to determine initial state
     * - Setting up the content width based on product count
     * - Adding focus event listeners to child elements for keyboard navigation
     *
     * @override
     */
    connectedCallback() {
        if (!this.isConnected)
            return;
        if (this.hasAttribute('open')) {
            const attr = this.getAttribute('open');
            this.open = attr === '' || attr.toLowerCase() === 'true';
        }
        if (this.open) {
            this.toggle(null, false);
        }
        const content = this.shadowRoot?.getElementById('content');
        this.productCount = this.childElementCount;
        content.style.setProperty('--content-width', `calc(100% + (${this.productCount} - var(--items-show)) * var(--item-width))`);
        for (let i = 0; i < this.children.length; i++) {
            this.children[i].addEventListener('focus', this.moveIntoView.bind(this));
        }
    }
    /**
     * Toggles the expand/collapse state of the variant shelf.
     *
     * When expanding, the shelf becomes visible and scales to full height with animation.
     * When collapsing, the animation plays in reverse. Also sends a tracking event
     * when the shelf is opened and toggles the arrow icon direction.
     *
     * @param event - The mouse event that triggered the toggle, or null for programmatic calls
     * @param useTransition - Whether to use CSS transitions for the animation (default: true)
     */
    toggle(event, useTransition = true) {
        const nd = this.shadowRoot?.querySelector('.ctnr');
        let classA = 'visible';
        let classB = 'expanded';
        let t = 0;
        if (nd.classList.contains('visible')) {
            [classB, classA] = [classA, classB];
            t = this.transitionDuration;
        }
        else {
            // send variantShelfOpen event
            tracker.track('variantShelfOpen', {});
        }
        nd.classList.toggle(classA);
        if (useTransition) {
            setTimeout(() => {
                nd.classList.toggle(classB);
            }, t);
        }
        else {
            nd.classList.toggle(classB);
        }
        this.shadowRoot?.getElementById('btn-picto')?.classList.toggle('reversed');
        if (event)
            this.toggleAttribute('open');
    }
    /**
     * Slides the content horizontally in the specified direction.
     *
     * Calculates the new offset based on the direction and product count, ensuring
     * the content stays within valid bounds. Updates the CSS transform property
     * to animate the content movement and enables/disables navigation buttons
     * based on the current position.
     *
     * @param dir - Direction to slide: -1 for left (previous), 1 for right (next)
     */
    slide(dir) {
        const content = this.shadowRoot?.getElementById('content');
        if (!content)
            return;
        let maxOffset = Math.min(0, content.parentElement.offsetWidth - content.scrollWidth) / content.scrollWidth * 100;
        const remainder = maxOffset * 100 % (100 / this.productCount * 100);
        if (remainder >= -9) {
            maxOffset = maxOffset - remainder / 100;
        }
        this._offset -= dir * (100 / this.productCount);
        this._offset = Math.max(Math.min(this._offset, 0), maxOffset);
        content.style.setProperty('--content-translate', `${this._offset}%`);
        const leftBtn = this.shadowRoot?.querySelector('.left');
        const rightBtn = this.shadowRoot?.querySelector('.right');
        if (this._offset === 0)
            leftBtn.setAttribute('disabled', '');
        else
            leftBtn.removeAttribute('disabled');
        if (Math.abs(this._offset - maxOffset) <= 0.01)
            rightBtn.setAttribute('disabled', '');
        else
            rightBtn.removeAttribute('disabled');
    }
    /**
     * Moves a focused element into the visible area of the shelf.
     *
     * This method is triggered by focus events on child elements. It compares
     * the position of the focused element with the visible container bounds
     * and automatically slides the content left or right to ensure the focused
     * element is fully visible. This provides keyboard navigation support.
     *
     * @param e - The focus event containing the target element that needs to be visible
     */
    moveIntoView(e) {
        const event = e;
        const ctDims = (this.shadowRoot?.querySelector('.ctnr')).getBoundingClientRect();
        const elDims = event.target.getBoundingClientRect();
        let dir = 0;
        if (elDims.left < ctDims.left) {
            dir = -1;
        }
        else if (elDims.right > ctDims.right) {
            dir = 1;
        }
        if (dir)
            this.slide(dir);
    }
}
customElements.define('awe-variant-shelf', AweVariantShelf);
